/**
 * Provides submission logging helpers for form 21-4138 state transitions.
 *
 * This module centralizes Datadog RUM logging for submission session, success, and
 * failure events, plus optional validation context behind feature flags.
 *
 * Keeping this logic isolated is important because it enables targeted debugging of
 * 21-4138 submission failures with minimal risk and without platform-level changes.
 */
import FEATURE_FLAG_NAMES from 'platform/utilities/feature-toggles/featureFlagNames';
import { RUM_ACTIONS, SUBMISSION_ERROR_STATUSES } from '../config/monitoring';

export const APP_VALIDATION_LOGS_FLAG =
  FEATURE_FLAG_NAMES.decisionReviewForm214138ValidationLogs;

export const APP_VISIBILITY_LOGS_FLAG =
  FEATURE_FLAG_NAMES.decisionReviewForm214138VisibilityLogs;

/**
 * Checks if app-level visibility logging is enabled via feature toggles.
 *
 * Visibility logs are low-risk, static path identifiers that track submission state
 * (session-start, success, failure). This function determines if the feature flag allows
 * visibility logs to be sent to Datadog RUM without containing form data.
 *
 * @param {Object} [featureToggles={}] - Feature toggles object from Redux state
 * @returns {boolean} True if visibility logging is enabled, false otherwise
 */
export function isAppVisibilityLogsEnabled(featureToggles = {}) {
  return featureToggles?.[APP_VISIBILITY_LOGS_FLAG] || false;
}

/**
 * Checks if app-level validation logging is enabled via feature toggles.
 *
 * Validation logs are higher-risk, PII-avoidant logs that include form field validation
 * states and context. This function determines if the feature flag allows these logs
 * to be sent to Datadog RUM, as they may contain sensitive form information.
 *
 * Provides independent gating separate from visibility logs for fine-grained control.
 * @param {Object} [featureToggles={}] - Feature toggles object from Redux state
 * @returns {boolean} True if validation logging is enabled, false otherwise
 */
export function isAppValidationLogsEnabled(featureToggles = {}) {
  return featureToggles?.[APP_VALIDATION_LOGS_FLAG] || false;
}

/**
 * Generates a unique identifier for this submission session.
 *
 * The session ID is used to correlate all logging events (submit pending, validation checks,
 * success/failure) that belong to a single submission flow. This enables filtering and
 * matching retries and outcomes to one submission session in Datadog.
 *
 * Uses crypto.randomUUID() when available, falling back to crypto.getRandomValues()
 * to build an RFC4122 v4 UUID. A final deterministic, counter-based fallback is used
 * only in environments without the Web Crypto API; it never relies on Math.random().
 * @returns {string} Unique session ID
 */
let sessionIdCounter = 0;

export function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    // Set version (4) and variant (RFC4122) bits using arithmetic to avoid bitwise ops.
    bytes[6] = (bytes[6] % 16) + 0x40;
    bytes[8] = (bytes[8] % 64) + 0x80;
    const hex = Array.from(bytes, byte =>
      byte.toString(16).padStart(2, '0'),
    ).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16,
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Final fallback for environments without the Web Crypto API. Combines a
  // timestamp with a monotonically increasing counter so IDs stay unique
  // without using Math.random().
  sessionIdCounter += 1;
  return `${Date.now()}-${sessionIdCounter}`;
}

/**
 * Sends a Datadog RUM action event with optional payload data.
 *
 * This is an internal helper that centralizes all RUM logging to a single function,
 * making it easy to conditionally enable/disable or modify RUM behavior across the
 * entire form logging system.
 *
 * Safely handles cases where Datadog RUM is not available on the page.
 * @param {string} action - The RUM action name (e.g., '4138_submission_attempt')
 * @param {Object} [payload={}] - Event payload data to log (sessionId, form context, etc.)
 * @returns {void}
 */
function logRumAction(action, payload = {}) {
  window.DD_RUM?.addAction(action, payload);
}

/**
 * Validates that a name field contains non-empty, non-whitespace text.
 *
 * Name fields must be strings with actual content (not just spaces) to count as valid.
 * This prevents false positives from whitespace-only or empty string values that might
 * appear in form data due to user input or form initialization quirks.
 *
 * Handles both populated and missing name fields safely.
 * @param {*} value - The value to validate (typically from formData.fieldName.first/last)
 * @returns {boolean} True if value is a non-empty, non-whitespace string; false otherwise
 */
export function isValidNameField(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Checks whether a full name is valid, i.e. it has at least one usable part.
 *
 * Returns true when either the first or last name is a non-empty, non-whitespace
 * string. Handles missing name objects safely.
 *
 * @param {Object} [name] - A name object with first/last fields
 * @returns {boolean} True if the first or last name is valid; false otherwise
 */
export function isValidFullName(name) {
  return isValidNameField(name?.first) || isValidNameField(name?.last);
}

/**
 * Checks whether a name has any valid parts (first or last).
 *
 * Returns true when either the first or last name is a non-empty, non-whitespace
 * string. Handles missing name objects safely.
 *
 * @param {Object} [name] - A name object with first/last fields
 * @returns {boolean} True if the first or last name is valid; false otherwise
 */
export function hasValidNameParts(name) {
  return isValidNameField(name?.first) || isValidNameField(name?.last);
}

/**
 * Picks a single valid name part, backfilling from the profile when needed.
 *
 * Returns the submitted part when it is a non-empty, non-whitespace string;
 * otherwise falls back to the profile part.
 *
 * @param {*} submittedPart - The submitted first/last value
 * @param {*} profilePart - The profile first/last value to fall back to
 * @returns {*} The submitted part when valid, otherwise the profile part
 */
export function fillName(submittedPart, profilePart) {
  return isValidNameField(submittedPart) ? submittedPart : profilePart;
}

/**
 * Merges a partially-valid submitted name with a profile name, field by field.
 *
 * Keeps each valid part of the submitted name and backfills only the missing
 * (blank/whitespace) first or last from the profile. The middle name is
 * intentionally not backfilled. Use this when the submitted name has at least
 * one valid part but is missing the other.
 *
 * @param {Object} [submitted] - The submitted name (first/last, at least one valid)
 * @param {Object} [profile] - The profile name used to backfill missing parts
 * @returns {Object} A name object with valid first/last parts
 */
export function handlePartialName(submitted, profile) {
  return {
    ...submitted,
    first: fillName(submitted?.first, profile?.first),
    last: fillName(submitted?.last, profile?.last),
  };
}

/**
 * Checks whether a name field is empty at submit time.
 *
 * Treats missing, non-string, empty (''), and whitespace-only ('   ') values all as
 * empty. This is the positive-sense inverse of isValidNameField and is used to surface
 * an intuitive "the first name is empty" signal in the validation logs.
 *
 * @param {*} value - The value to check (typically submittedData.fullName.first)
 * @returns {boolean} True if the value is missing, empty, or whitespace-only
 */
function isEmptyNameField(value) {
  return !isValidNameField(value);
}

/**
 * Checks whether a name field contains only whitespace.
 *
 * Returns true only when the value is a non-empty string that trims to nothing
 * (e.g. '   '). This distinguishes the "user typed only spaces" case from a field
 * that was genuinely missing or an empty string, which both return false here.
 *
 * @param {*} value - The value to check (typically submittedData.fullName.first)
 * @returns {boolean} True only if the value is a whitespace-only string
 */
function isWhitespaceOnlyNameField(value) {
  return (
    typeof value === 'string' && value.length > 0 && value.trim().length === 0
  );
}

/**
 * Determines the source of the claimant name as it will be submitted to the API.
 *
 * Analyzes the submitted data shape (after transformation) to understand whether the
 * claimant name being sent to the backend came from VA profile prefill or manual entry.
 * This helps diagnose if prefill data is correctly populated vs. user-entered fallbacks.
 *
 * Note: submitted data does not contain 'view:' fields; those are frontend-only markers.
 * For veterans filing with prefill, the submitted fullName equals the prefilled profile name.
 * @param {Object} [submittedData={}] - The transformed form data about to be sent to the API
 * @param {Object} [reduxFormData={}] - The Redux form state (used to detect if prefill occurred)
 * @returns {string} One of: 'prefilled' | 'manual' | 'empty'
 */
function getClaimantNameSource(submittedData = {}, reduxFormData = {}) {
  // Prefill indicator: if Redux has view:profileFullName, then submitted fullName came from prefill
  if (
    reduxFormData?.['view:profileFullName']?.first ||
    reduxFormData?.['view:profileFullName']?.last
  ) {
    return 'prefilled';
  }
  if (submittedData?.fullName?.first || submittedData?.fullName?.last) {
    return 'manual';
  }
  return 'empty';
}

/**
 * Determines the source of the veteran name as it will be submitted to the API.
 *
 * Only applies when a non-veteran claimant is filing for a veteran. Analyzes whether
 * the submitted veteranFullName came from prefill (claimant is the veteran, has profile)
 * or manual entry (claimant is someone else entering veteran's info).
 *
 * Note: submitted data does not contain 'view:' fields; prefill is inferred from Redux state.
 * @param {Object} [submittedData={}] - The transformed form data about to be sent to the API
 * @param {Object} [reduxFormData={}] - The Redux form state (used to detect prefill)
 * @returns {string} One of: 'prefilled' | 'manual' | 'empty'
 */
function getVeteranNameSource(submittedData = {}, reduxFormData = {}) {
  // For veterans filing for themselves: veteranFullName equals prefilled profile name
  if (
    reduxFormData?.['view:profileFullName']?.first &&
    isValidNameField(reduxFormData?.['view:profileFullName']?.first)
  ) {
    return 'prefilled';
  }
  // For non-veterans filing for a veteran: they manually entered veteranFullName
  if (
    submittedData?.veteranFullName?.first ||
    submittedData?.veteranFullName?.last
  ) {
    return 'manual';
  }
  return 'empty';
}

/**
 * Builds the empty/whitespace-only flag pair for a single name field.
 *
 * Centralizes the per-field logging shape so every name field (claimant/veteran,
 * first/last) reports the same two signals: whether it is empty at submit time
 * (missing, empty string, or whitespace-only) and, more specifically, whether it
 * contains only whitespace. Together these fully describe the field's emptiness, so
 * no separate "exists" flag is needed.
 *
 * @param {string} prefix - Field name prefix used to key the returned flags (e.g. 'claimantFirstName')
 * @param {*} value - The field value to evaluate (typically submittedData.fullName.first)
 * @returns {Object} Object with `${prefix}IsEmpty` and `${prefix}WhitespaceOnly` booleans
 */
function buildNameFieldFlags(prefix, value) {
  return {
    [`${prefix}IsEmpty`]: isEmptyNameField(value),
    [`${prefix}WhitespaceOnly`]: isWhitespaceOnlyNameField(value),
  };
}

/**
 * Checks the empty/whitespace state of the prefill (VA profile) name fields.
 *
 * Looks at the prefill source location (view:profileFullName in Redux form data)
 * rather than the submitted data, so that when a name is prefilled we can tell whether
 * the prefill value itself was empty or whitespace-only. This helps distinguish a bad
 * prefill (profile delivered blank/whitespace) from a user clearing or mistyping the
 * field after prefill.
 *
 * Reuses buildNameFieldFlags so the prefill flags mirror the submitted-field flags.
 * @param {Object} [reduxFormData={}] - The Redux form state (source of view:profileFullName)
 * @returns {Object} Object with profileFirstNameIsEmpty, profileFirstNameWhitespaceOnly, profileLastNameIsEmpty, profileLastNameWhitespaceOnly
 */
function checkPrefillNameStates(reduxFormData = {}) {
  const profileFullName = reduxFormData?.['view:profileFullName'] || {};

  return {
    ...buildNameFieldFlags('profileFirstName', profileFullName?.first),
    ...buildNameFieldFlags('profileLastName', profileFullName?.last),
  };
}

/**
 * Checks the empty/whitespace state and source of the claimant name fields.
 *
 * Reports per-field IsEmpty/WhitespaceOnly flags for the claimant first and last names
 * as they will be submitted to the API, plus the name-source classification.
 * @param {Object} [submittedData={}] - The transformed form data about to be sent to the API
 * @param {Object} [reduxFormData={}] - The Redux form state (used for prefill detection)
 * @returns {Object} Object with claimantFirstNameIsEmpty, claimantFirstNameWhitespaceOnly, claimantLastNameIsEmpty, claimantLastNameWhitespaceOnly, claimantNameSource
 */
function checkClaimantNameStates(submittedData = {}, reduxFormData = {}) {
  return {
    ...buildNameFieldFlags('claimantFirstName', submittedData?.fullName?.first),
    ...buildNameFieldFlags('claimantLastName', submittedData?.fullName?.last),
    claimantNameSource: getClaimantNameSource(submittedData, reduxFormData),
  };
}

/**
 * Checks the empty/whitespace state and source of the veteran name fields.
 *
 * Reports per-field IsEmpty/WhitespaceOnly flags for the veteran first and last names
 * as they will be submitted to the API, plus the name-source classification.
 * @param {Object} [submittedData={}] - The transformed form data about to be sent to the API
 * @param {Object} [reduxFormData={}] - The Redux form state (used for prefill detection)
 * @returns {Object} Object with veteranFirstNameIsEmpty, veteranFirstNameWhitespaceOnly, veteranLastNameIsEmpty, veteranLastNameWhitespaceOnly, veteranNameSource
 */
function checkVeteranNameStates(submittedData = {}, reduxFormData = {}) {
  return {
    ...buildNameFieldFlags(
      'veteranFirstName',
      submittedData?.veteranFullName?.first,
    ),
    ...buildNameFieldFlags(
      'veteranLastName',
      submittedData?.veteranFullName?.last,
    ),
    veteranNameSource: getVeteranNameSource(submittedData, reduxFormData),
  };
}

/**
 * Checks the state of all name fields as they will be submitted to the API.
 *
 * Examines the transformed/submitted data shape (no 'view:' fields, names possibly
 * truncated to API limits) to provide a snapshot of what the backend will receive.
 * Different name scenarios (empty, partial, prefilled vs. manual, truncation effects)
 * produce different failure modes on the backend, and this data helps correlate
 * submission failures to specific data conditions.
 *
 * For each name field (claimant/veteran first and last) it reports an IsEmpty flag
 * (missing, empty, or whitespace-only) and a WhitespaceOnly flag, plus a name-source
 * classification for each name. It also reports the empty/whitespace state of the
 * prefill (VA profile) name fields so prefilled-but-blank values can be detected.
 * @param {Object} [submittedData={}] - The transformed form data about to be sent to the API
 * @param {Object} [reduxFormData={}] - The Redux form state (used for prefill detection)
 * @returns {Object} Object with claimantFirstNameIsEmpty, claimantFirstNameWhitespaceOnly, claimantLastNameIsEmpty, claimantLastNameWhitespaceOnly, claimantNameSource, veteranFirstNameIsEmpty, veteranFirstNameWhitespaceOnly, veteranLastNameIsEmpty, veteranLastNameWhitespaceOnly, veteranNameSource, profileFirstNameIsEmpty, profileFirstNameWhitespaceOnly, profileLastNameIsEmpty, profileLastNameWhitespaceOnly
 */
function checkNameStates(submittedData = {}, reduxFormData = {}) {
  return {
    ...checkClaimantNameStates(submittedData, reduxFormData),
    ...checkVeteranNameStates(submittedData, reduxFormData),
    ...checkPrefillNameStates(reduxFormData),
  };
}

/**
 * Determines the form's progress state (new submission or continuation of saved form).
 *
 * New form submissions and resumed/continued forms may have different failure patterns.
 * Tracking whether a user is filling out a fresh form or resuming a saved in-progress
 * session helps identify whether state persistence, form recovery, or initialization is
 * involved in submission failures.
 *
 * Presence of inProgressFormId indicates the form is a saved session being continued.
 * @param {string} [inProgressFormId] - The saved form session ID from Redux state, or undefined
 * @returns {string} One of: 'inProgress' (form is continued from saved session) | 'new' (fresh submission)
 */
function getFormProgressStatus(inProgressFormId) {
  return inProgressFormId ? 'inProgress' : 'new';
}

/**
 * Builds the validation log payload from the transformed/submitted data.
 *
 * Centralizes the higher-risk validation payload shape so the submitPending and
 * failure validation logs emit identical form context, keyed to the same session ID.
 * Used only by the validation logging path. Includes reason: status so the
 * triggering submission outcome is preserved on validation logs as well.
 * @param {Object} config - Validation payload context
 * @param {string} [config.status] - Triggering submission status (logged as reason)
 * @param {string} [config.sessionId] - Session ID correlating this submission flow
 * @param {Object} [config.formData={}] - Redux form data (for prefill detection)
 * @param {Object} [config.submittedData={}] - Transformed form data (as sent to the API)
 * @param {string} [config.inProgressFormId] - Saved form session ID
 * @returns {Object} Validation log payload
 */
function buildValidationPayload({
  status,
  sessionId,
  formData,
  submittedData,
  inProgressFormId,
}) {
  return {
    reason: status,
    sessionId,
    formId: '21-4138',
    statementType: submittedData?.statementType,
    formProgressStatus: getFormProgressStatus(inProgressFormId),
    ...checkNameStates(submittedData, formData),
  };
}

/**
 * Resolves the submission session ID for the current status transition.
 *
 * Session ID lifecycle is independent of any logging: it is generated/reused on
 * submitPending (kept across failures and retries) and cleared after a successful
 * submission so the next attempt starts a fresh session.
 * @param {string} status - Current submission status from Redux
 * @param {string} [currentSessionId] - Existing session ID from the caller
 * @returns {string|null} The session ID to use for this transition
 */
function resolveSessionId(status, currentSessionId) {
  if (status === 'submitPending') {
    return currentSessionId || generateSessionId();
  }
  return currentSessionId;
}

/**
 * Emits low-risk visibility logs for a submission status transition.
 *
 * Visibility logs are static path identifiers (attempt/success/failure) carrying
 * only the correlating session ID and never form data. This path is fully separate
 * from validation logging and gated by its own feature flag so the low-risk tier
 * can be enabled without exposing any higher-risk form context.
 *
 * Every visibility log includes reason: status so the submission outcome (the
 * triggering status) is preserved for monitoring/debugging across all events.
 * @param {Object} config - Visibility logging context
 * @param {string} config.status - Current submission status from Redux
 * @param {string} [config.sessionId] - Session ID correlating this submission flow
 * @returns {void}
 */
function logVisibilityEvent({ status, sessionId }) {
  if (status === 'submitPending') {
    logRumAction(RUM_ACTIONS.SUBMISSION_ATTEMPT, { reason: status, sessionId });
  } else if (status === 'applicationSubmitted') {
    logRumAction(RUM_ACTIONS.SUBMISSION_SUCCESS, { reason: status, sessionId });
  } else if (SUBMISSION_ERROR_STATUSES.has(status)) {
    logRumAction(RUM_ACTIONS.SUBMISSION_FAILURE, { reason: status, sessionId });
  }
}

/**
 * Emits higher-risk validation logs for a submission status transition.
 *
 * Validation logs include transformed/submitted form context and run only on
 * submitPending and failure statuses. This path is fully separate from visibility
 * logging and gated by its own feature flag so the higher-risk tier never piggybacks
 * on the low-risk path.
 *
 * The submitted data is produced lazily via getSubmittedData and only after a
 * qualifying status is confirmed, so the (relatively expensive) submit transform
 * runs solely when a validation log is actually about to be emitted.
 * @param {Object} config - Validation logging context
 * @param {string} config.status - Current submission status from Redux
 * @param {string} [config.sessionId] - Session ID correlating this submission flow
 * @param {Object} [config.formData={}] - Redux form data object (for prefill detection)
 * @param {Function} [config.getSubmittedData] - Lazily returns the transformed form data (as sent to API)
 * @param {string} [config.inProgressFormId] - Saved form session ID
 * @returns {void}
 */
function logValidationEvent({
  status,
  sessionId,
  formData,
  getSubmittedData,
  inProgressFormId,
}) {
  let action;
  if (status === 'submitPending') {
    action = RUM_ACTIONS.SUBMISSION_ATTEMPT;
  } else if (SUBMISSION_ERROR_STATUSES.has(status)) {
    action = RUM_ACTIONS.SUBMISSION_FAILURE;
  } else {
    return;
  }

  logRumAction(
    action,
    buildValidationPayload({
      status,
      sessionId,
      formData,
      submittedData: getSubmittedData?.(),
      inProgressFormId,
    }),
  );
}

/**
 * Tracks submission state transitions and logs relevant events to Datadog RUM.
 *
 * This is the main entry point for form 21-4138 submission logging. It observes
 * Redux submission state changes (submitPending to success/failure), generates or
 * reuses a submission session ID for correlation, and conditionally logs visibility (path)
 * and validation (form context) events based on feature flags. No logging occurs
 * without explicit feature flag permission, ensuring PII stays private by default.
 *
 * Visibility logs are always based on submission status (low-risk, static paths).
 * Validation logs use the transformed/submitted data shape (what the API receives),
 * allowing frontend validation context to match actual backend expectations.
 *
 * Generates/reuses a session ID on submitPending, keeps it across failures/retries,
 * and resets it only after a successful submission.
 * @param {Object} config - Configuration object
 * @param {boolean} config.isVisibilityEnabled - Feature flag: enable path visibility logs
 * @param {boolean} config.isValidationEnabled - Feature flag: enable form validation logs
 * @param {string} config.status - Current submission status from Redux
 * @param {string} [config.currentSessionId] - Current session ID (from ref, used for success/failure)
 * @param {Object} [config.formData={}] - Redux form data object (for prefill detection in validation logs)
 * @param {Function} [config.getSubmittedData] - Lazily returns transformed form data; only invoked when a validation log is emitted
 * @param {string} [config.inProgressFormId] - Saved form session ID from Redux state
 * @returns {string|null} Next session ID state for caller to persist
 */
export function trackSubmissionSession({
  isVisibilityEnabled,
  isValidationEnabled,
  status,
  currentSessionId,
  formData,
  getSubmittedData,
  inProgressFormId,
}) {
  // Session ID lifecycle is managed independently of logging so correlation
  // stays consistent regardless of which feature flags are enabled.
  const sessionId = resolveSessionId(status, currentSessionId);

  // Visibility (low-risk) and validation (higher-risk) are handled on completely
  // separate paths, each gated by its own feature flag. Logging is wrapped in a
  // try/catch so that a failure in the (relatively expensive) submit transform
  // or RUM call can never bubble up to the calling effect and crash the form.
  try {
    if (isVisibilityEnabled) {
      logVisibilityEvent({ status, sessionId });
    }

    if (isValidationEnabled) {
      logValidationEvent({
        status,
        sessionId,
        formData,
        getSubmittedData,
        inProgressFormId,
      });
    }
  } catch (error) {
    // Surface the logging failure itself to RUM (no form data is included in
    // the error) so the gap in observability is visible, then swallow it.
    window.DD_RUM?.addError?.(error);
  }

  // Reset the session after a successful submission; keep it across retries.
  return status === 'applicationSubmitted' ? null : sessionId;
}
